import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Heart,
  PawPrint,
  Baby,
  Leaf,
  Sun,
  ArrowUpRight,
} from "lucide-react";

export default function Safety() {
  return (
    <div data-testid="safety-page">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--terracotta)]/10 text-[var(--terracotta)] text-xs font-semibold mb-6">
          <AlertTriangle className="w-3.5 h-3.5" /> Important — please read
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl text-[var(--ink-forest)] tracking-tight leading-[1.05]">
          Using oils
          <span className="italic text-[var(--terracotta)]"> with care.</span>
        </h1>
        <p className="mt-6 text-[var(--muted-fg)] leading-relaxed text-lg max-w-2xl mx-auto">
          Essential oils are powerful plant medicine — beautiful when used thoughtfully, harmful
          when used carelessly. Here&apos;s what every shopper should know.
        </p>
      </section>

      {/* Quick rules */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-10">
        <p className="eyebrow mb-5 text-center">Five rules to use anything from our shelf</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Rule
            icon={Leaf}
            title="Always dilute"
            body="Never apply undiluted essential oils to skin. A safe topical dilution is 1-3% (about 6-18 drops per 1oz of carrier oil)."
          />
          <Rule
            icon={Sun}
            title="Mind the sun"
            body="Citrus oils (lemon, orange, bergamot) are photosensitive. Avoid sun exposure for 12 hours after applying to skin."
          />
          <Rule
            icon={Heart}
            title="Patch test first"
            body="Test new oils on a small area of inner forearm and wait 24 hours before regular use. Stop if you see any reaction."
          />
          <Rule
            icon={Baby}
            title="Children & pregnancy"
            body="Many oils are not safe for children under 6 or during pregnancy. When in doubt, consult a qualified aromatherapist or doctor."
          />
          <Rule
            icon={PawPrint}
            title="Pets are different"
            body="Some oils that are calming for humans are dangerous for pets — especially cats. See the section below before diffusing."
          />
          <Rule
            icon={AlertTriangle}
            title="Never ingest"
            body="Unless guided by a certified clinical aromatherapist, do not take essential oils internally. Topical and aromatic use only."
          />
        </div>
      </section>

      {/* Pet safety — featured callout */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24" data-testid="pet-safety-section">
        <div className="bg-[var(--ink-forest)] text-[var(--bg-cream)] rounded-[2rem] p-10 lg:p-14">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--gold)]/15 text-[var(--gold)] text-xs font-semibold mb-5">
                <PawPrint className="w-3.5 h-3.5" /> For pet families
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
                If you live with a furry friend, please read this.
              </h2>
              <p className="mt-5 text-[var(--bg-cream)]/80 leading-relaxed">
                Essential oils that are calming for humans can be genuinely dangerous for cats,
                dogs, birds, and small animals. Their bodies process compounds differently than
                ours — especially cats, who lack the liver enzyme to break down many oils.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <div className="bg-[var(--bg-cream)]/5 border border-[var(--bg-cream)]/10 rounded-2xl p-5">
                <p className="eyebrow text-[var(--gold)] mb-2">Toxic to cats — avoid entirely</p>
                <p className="text-sm text-[var(--bg-cream)]/85 leading-relaxed">
                  Tea tree, peppermint, eucalyptus, citrus oils (lemon, sweet orange, bergamot),
                  pine, wintergreen, ylang ylang, cinnamon, clove, thyme. Even diffusing in a
                  shared room can be harmful.
                </p>
              </div>
              <div className="bg-[var(--bg-cream)]/5 border border-[var(--bg-cream)]/10 rounded-2xl p-5">
                <p className="eyebrow text-[var(--gold)] mb-2">Toxic to dogs — avoid</p>
                <p className="text-sm text-[var(--bg-cream)]/85 leading-relaxed">
                  Tea tree, pennyroyal, wintergreen, pine, anise, clove, cinnamon, and citrus oils
                  in concentration.
                </p>
              </div>
              <div className="bg-[var(--bg-cream)]/5 border border-[var(--bg-cream)]/10 rounded-2xl p-5">
                <p className="eyebrow text-[var(--gold)] mb-2">Always do</p>
                <ul className="text-sm text-[var(--bg-cream)]/85 leading-relaxed space-y-1.5 list-disc list-inside">
                  <li>Diffuse in a well-ventilated room your pet can leave at any time</li>
                  <li>Never apply oils directly to a pet&apos;s skin or fur</li>
                  <li>Store oils where pets cannot reach them</li>
                  <li>If your pet shows any symptoms (drooling, vomiting, lethargy), stop immediately and call your vet</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Paws & Harmony cross-promo */}
          <div className="mt-10 pt-8 border-t border-[var(--bg-cream)]/15 flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <p className="eyebrow text-[var(--gold)] mb-2">A sister sanctuary</p>
              <p className="font-serif text-2xl text-[var(--bg-cream)] leading-tight">
                Looking for a way to calm and connect with your pet that doesn&apos;t involve oils?
              </p>
              <p className="mt-2 text-sm text-[var(--bg-cream)]/75">
                Visit our sister site Paws & Harmony — original songs, illustrations, and stories
                made for the pets and people who hold each other through every season.
              </p>
            </div>
            <a
              href="https://profile-sync-error.emergent.host"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[var(--gold)] text-[var(--ink-forest)] rounded-full px-6 py-3 text-sm font-semibold hover:bg-[var(--terracotta)] hover:text-white transition-colors"
              data-testid="paws-harmony-link"
            >
              Visit Paws & Harmony <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 mt-20 mb-16">
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-7 text-sm text-[var(--muted-fg)] leading-relaxed">
          <p className="eyebrow mb-3 text-[var(--ink-forest)]">A friendly disclaimer</p>
          <p>
            The information on this site is for educational purposes and is not a substitute for
            advice from a qualified medical professional, certified clinical aromatherapist, or
            veterinarian. If you have a health condition, are pregnant or nursing, or have concerns
            about a pet, please consult a professional before use.
          </p>
          <p className="mt-3">
            Have questions?{" "}
            <a href="mailto:hello@dcgiftworldtoo.com" className="text-[var(--terracotta)] underline">
              Reach us at hello@dcgiftworldtoo.com
            </a>{" "}
            — we&apos;re always happy to help.
          </p>
        </div>
      </section>
    </div>
  );
}

function Rule({ icon: Icon, title, body }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6">
      <div className="w-10 h-10 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[var(--terracotta)]" />
      </div>
      <h3 className="font-serif text-xl text-[var(--ink-forest)] mb-2">{title}</h3>
      <p className="text-sm text-[var(--muted-fg)] leading-relaxed">{body}</p>
    </div>
  );
}
